import { useApolloClient } from '@apollo/client';
import { Descriptions, Grid, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type MaintenanceModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/MaintenanceModuleWithPermissionsSpecs';
import { type UploadFileWithPreviewFormDataFragment } from '../../../../api/fragments/UploadFileWithPreviewFormData';
import {
    UpdateMaintenanceModuleDocument,
    type UpdateMaintenanceModuleMutation,
    type UpdateMaintenanceModuleMutationVariables,
} from '../../../../api/mutations/updateMaintenanceModule';
import { type MaintenanceModuleSettings } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import MaintainenceModuleMainDetailsForm from './MaintainenceModuleMainDetailsForm';
import MaintenanceModuleActions from './MaintenanceModuleActions';
import MaintenanceModuleFooter from './MaintenanceModuleFooter';

export type FormValues = MaintenanceModuleSettings & {
    image?: File | UploadFileWithPreviewFormDataFragment;
};
export type MaintenanceModulePageProps = { module: MaintenanceModuleWithPermissionsSpecsFragment };

const MaintenanceModulePage = ({ module }: MaintenanceModulePageProps) => {
    const { t } = useTranslation(['maintenance', 'moduleDetails', 'common']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({ versioning: module.versioning, moduleId: module.id });

    const permissions = {
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };
    const screens = Grid.useBreakpoint();

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('moduleDetails:fields.displayName.label')}>
                {module.displayName}
            </Descriptions.Item>

            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    const initialValues = useMemo((): FormValues => pick(['displayName'], module), [module]);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<MaintenanceModuleSettings>(
        async values => {
            // submitting message
            message.loading({
                content: t('maintenance:messages.updateMaintainenceDetailsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit creation
            await apolloClient
                .mutate<UpdateMaintenanceModuleMutation, UpdateMaintenanceModuleMutationVariables>({
                    mutation: UpdateMaintenanceModuleDocument,
                    variables: {
                        moduleId: module.id,
                        settings: pick(['displayName'], values),
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('maintenance:messages.updateMaintainenceDetailsSuccessful'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, t]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<MaintenanceModuleActions module={module} permissions={permissions} />}
            footer={[<MaintenanceModuleFooter />]}
            onBack={() => navigate('/admin/system/modules')}
            title={screens.md ? t('maintenance:moduleTitle', { name: module.displayName }) : module.displayName}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="updateMainDetails" name="updateMainDetails" onSubmitCapture={handleSubmit}>
                        <MaintainenceModuleMainDetailsForm />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default MaintenanceModulePage;
